import { useState, useEffect, useMemo } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { airports } from '../lib/airports';

// Hook customizado para "debouncing"
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

export default function AirportAutocomplete({ value, onSelect, placeholder = "Selecione..." }) {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");
    const debouncedSearchQuery = useDebounce(inputValue, 300);

    const selectedAirport = useMemo(() => 
        airports.find(airport => airport.iata.toLowerCase() === value?.toLowerCase()),
    [value]);

    const filteredAirports = useMemo(() => {
        if (!debouncedSearchQuery) {
            return airports.slice(0, 50);
        }
        const lowercasedQuery = debouncedSearchQuery.toLowerCase();
        return airports.filter(airport =>
            airport.iata.toLowerCase().includes(lowercasedQuery) ||
            airport.name.toLowerCase().includes(lowercasedQuery) ||
            airport.city.toLowerCase().includes(lowercasedQuery)
        ).slice(0, 50);
    }, [debouncedSearchQuery]);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={open}
                    className="w-full justify-between font-normal"
                >
                    {selectedAirport
                        ? `${selectedAirport.iata} - ${selectedAirport.city}`
                        : placeholder}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[300px] p-0">
                <div className="p-2 border-b">
                    <Input
                        placeholder="Buscar por IATA, cidade..."
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        className="w-full"
                        autoFocus
                    />
                </div>
                <div className="max-h-[300px] overflow-y-auto">
                    {filteredAirports.length > 0 ? (
                        filteredAirports.map((airport) => (
                            <button
                                key={airport.iata}
                                onClick={() => {
                                    onSelect(airport.iata);
                                    setOpen(false);
                                    setInputValue("");
                                }}
                                className="w-full text-left p-2 hover:bg-gray-100 flex items-center justify-between rounded-sm"
                            >
                                <div className="flex flex-col">
                                    <span className="font-medium text-sm">{airport.iata} - {airport.city}</span>
                                    <span className="text-xs text-gray-500">{airport.name}</span>
                                </div>
                                <Check
                                    className={cn(
                                        "h-4 w-4",
                                        value?.toLowerCase() === airport.iata.toLowerCase() ? "opacity-100" : "opacity-0"
                                    )}
                                />
                            </button>
                        ))
                    ) : (
                        <p className="p-4 text-sm text-center text-gray-500">Nenhum aeroporto encontrado.</p>
                    )}
                </div>
            </PopoverContent>
        </Popover>
    );
}

